import classNames from "classnames";
import React, { type JSX } from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconAddIcon from "../../../img/icons/add_icon.svg";
import iconReviewsYes from "../../../img/icons/reviews-yes.svg";
import { getProduct, getProductURL } from "../../api/products";
import {
    IPLCOptionPanel,
    IPLCProductCategorySelector,
    IPLCProductCategorySelectorOption,
} from "../../apps/configurator/models";
import { ISelectedCategories } from "../../apps/configurator/reducers.interfaces";
import { Flair } from "../../common/Flair";
import { Image } from "../../common/Image";
import { RichText } from "../../common/RichText";
import {
    IOptionCode,
    IOptionSingleValue,
    IOptionValues,
    IProduct,
} from "../../models/catalogue";
import {
    IProductCategoryID,
    IProductID,
    isoSafeHTML,
} from "../../models/nominals";
import { BreakPoint, IViewport } from "../../models/screen";
import {
    IComparisonCardAttribute,
    IComparisonCardCategory,
    IComparisonCardProduct,
    IComparisonTabAttribute,
    IComparisonTabCategory,
    IComparisonTabProduct,
    ITabCardsBlock,
} from "../../models/streamfield-blocks";
import { notEmpty, unique } from "../../utils/functional";

import styles from "./TabCardsBlock.module.scss";

interface IPLCProductCategory {
    id: IProductCategoryID;
    name: string;
}

// Map old theme names to new CSS module-compatible names
const THEME_MAP: Record<string, string> = {
    "tab-cards-block--black-gradient": "tabCardsBlockBlackGradient",
    "tab-cards-block--light-blue": "tabCardsBlockLightBlue",
    "tab-cards-block--mint": "tabCardsBlockMint",
    "tab-cards-block--adapt-emerald-gradient":
        "tabCardsBlockAdaptEmeraldGradient",
};

// The following is a list of optionCodes that have unique button styling per TSI request.
// These attributes don't require a value (as they're just booleans).
const BOOLEAN_OPTION_CODES = [
    "option_chill",
    "option_advanced_support",
    "option_advanced_pressure_relief",
];

function getCategorySelector(
    pageCategorySelectors: readonly IPLCProductCategorySelector[],
    category: IPLCProductCategory,
): IPLCProductCategorySelector | undefined {
    return pageCategorySelectors.find(
        (categorySelector: IPLCProductCategorySelector) => {
            return categorySelector.value.options.find(
                (categorySelectorOption: IPLCProductCategorySelectorOption) => {
                    return (
                        categorySelectorOption.category.name === category.name
                    );
                },
            );
        },
    );
}

function getOptionValues(
    pageRootProducts: readonly IProduct[] | null,
    attributeCode: IOptionCode,
): IOptionSingleValue[] {
    if (!pageRootProducts) {
        return [];
    }

    let allOptionValues: IOptionSingleValue[] = [];

    pageRootProducts.forEach((product: IProduct) => {
        if (!product) {
            return [];
        }

        const children = product.children;
        if (!children || children.length <= 0) {
            const attr = product.attributes[attributeCode];
            if (!attr) {
                return [];
            }
            return Array.isArray(attr.value) ? attr.value : [attr.value];
        }
        const values = children
            .reduce<Array<IOptionSingleValue | null>>((memo, child) => {
                const attr = child.attributes[attributeCode];
                return memo.concat(attr ? attr.value : null);
            }, [])
            .filter(notEmpty)
            .filter(unique);
        values.forEach((value: IOptionSingleValue) => {
            allOptionValues.push(value);
        });

        return [];
    });

    allOptionValues = allOptionValues.filter(unique);

    return allOptionValues;
}

function hasOptionValue(
    pageRootProducts: readonly IProduct[] | null,
    attributeCode: IOptionCode,
    attributeValue: string,
): boolean {
    return getOptionValues(pageRootProducts, attributeCode).includes(
        attributeValue,
    );
}

function hasOptionPanel(
    pageRootProducts: readonly IProduct[] | null,
    attributeCode: IOptionCode,
): boolean {
    return !!getOptionValues(pageRootProducts, attributeCode).length;
}

function isMobileWidth(viewport: IViewport): boolean {
    return viewport.width <= BreakPoint.SMALL;
}

function isMobileOrTabletWidth(viewport: IViewport): boolean {
    return viewport.width <= BreakPoint.MEDIUM;
}

function renderHeader(
    tabCards: ITabCardsBlock,
    viewport: IViewport,
): React.JSX.Element {
    const headerClasses = classNames(styles.header, "l-capped-width");
    if (isMobileWidth(viewport)) {
        const headerMobile = tabCards.header_content_mobile || "";
        return <RichText className={headerClasses} html={headerMobile} />;
    }
    const headerDesktop = tabCards.header_content_desktop || "";
    return <RichText className={headerClasses} html={headerDesktop} />;
}

function renderNav(
    tabCards: ITabCardsBlock,
    viewport: IViewport,
    currentTab: number,
    onTabPress: (event: React.MouseEvent<HTMLElement>, tabNum: number) => void,
    onTabSelect: (event: React.FormEvent<HTMLSelectElement>) => void,
): React.JSX.Element | null {
    if (tabCards.hide_tabs) {
        return null;
    }
    let ariaLabel: string;
    let tabNames: JSX.Element[];

    if (isMobileWidth(viewport)) {
        ariaLabel =
            tabCards.aria_label || "tab cards block navigation - dropdown";
        tabNames = tabCards.tabs.map((tab, i) => {
            return (
                <option
                    key={i}
                    value={i}
                    dangerouslySetInnerHTML={{
                        __html: isoSafeHTML.unwrap(tab.value.tab_name),
                    }}
                ></option>
            );
        });
        return (
            <select
                className={styles.navSelect}
                dir="ltr"
                value={currentTab}
                onChange={onTabSelect}
            >
                {tabNames}
            </select>
        );
    }

    ariaLabel = tabCards.aria_label || "tab cards block navigation - tabs";
    tabNames = tabCards.tabs.map((tab, i) => {
        const isSelected = i === currentTab ? "selected" : "";
        return (
            <button
                key={i}
                className={isSelected}
                onClick={(e) => {
                    onTabPress(e, i);
                }}
                dangerouslySetInnerHTML={{
                    __html: isoSafeHTML.unwrap(tab.value.tab_name),
                }}
            ></button>
        );
    });
    return (
        <nav className={styles.nav} role="tablist" aria-label={ariaLabel}>
            {tabNames}
            <span></span>
        </nav>
    );
}

function renderTabCardButtonProduct(
    tabCard: IComparisonCardProduct,
    pageRootProducts: readonly IProduct[] | null,
    pageRootProductID: IProductID | null,
    onProductSelect: (productNumber: IProductID) => void,
): React.JSX.Element | null {
    const isSelected = tabCard.card_product === pageRootProductID;

    const pageHasProduct = pageRootProducts
        ? pageRootProducts.some((rootProduct) => {
              return rootProduct.id === tabCard.card_product;
          })
        : false;
    // If the value of ProductChooserSubBlock isn't on this page, then don't render the button
    if (!pageHasProduct) {
        return null;
    }

    const buttonClasses = classNames({
        "button": true,
        "button--selected": isSelected,
    });
    return (
        pageRootProductID && (
            <button
                className={buttonClasses}
                onClick={() => {
                    onProductSelect(tabCard.card_product);
                }}
            >
                {isSelected ? t`Selected` : t`Select`}
            </button>
        )
    );
}

function renderTabCardButtonCategory(
    tabCard: IComparisonCardCategory,
    pageCategorySelectors: readonly IPLCProductCategorySelector[],
    pageSelectedCategories: ISelectedCategories,
    onCategorySelect: (category: IPLCProductCategory) => void,
): React.JSX.Element | null {
    let isSelected = false;
    const selector = getCategorySelector(
        pageCategorySelectors,
        tabCard.card_category,
    );

    // If the value of CategoryChooserSubBlock isnt on this page, then dont render the button
    if (!selector) {
        return null;
    } else {
        if (pageSelectedCategories[selector.id] === tabCard.card_category.id) {
            isSelected = true;
        }
    }
    const buttonClasses = classNames({
        "button": true,
        "button--selected": isSelected,
    });
    return (
        <button
            className={buttonClasses}
            onClick={() => {
                onCategorySelect(tabCard.card_category);
            }}
        >
            {isSelected ? t`Selected` : t`Select`}
        </button>
    );
}

function renderTabCardButtonAttribute(
    tabCard: IComparisonCardAttribute,
    pageRootProducts: readonly IProduct[] | null,
    pageSelectedOptionValues: IOptionValues,
    onAttributeSelect: (
        attributeCode: IOptionCode,
        attributeValue: string,
    ) => void,
): React.JSX.Element | null {
    let isSelected = false;
    const attribute: IOptionCode = tabCard.card_attribute as IOptionCode;
    const isBooleanType =
        Object.values(BOOLEAN_OPTION_CODES).includes(attribute);
    const buttonText = isBooleanType ? "Add" : "Select";

    // If the value of ProductOptionValueChoiceBlock is not present in the card's ProductAttributeChoice, then dont render the button
    if (
        !isBooleanType &&
        !hasOptionValue(
            pageRootProducts,
            attribute,
            tabCard.card_attribute_value,
        )
    ) {
        return null;
    }

    let cardValue = isBooleanType ? "1" : tabCard.card_attribute_value;
    if (
        hasOptionPanel(pageRootProducts, attribute) &&
        pageSelectedOptionValues[attribute] === cardValue
    ) {
        isSelected = true;

        // If a boolean type card is already selected, set it's value to 'false' to allow toggling off
        if (isBooleanType) {
            cardValue = "0";
        }
    }

    const selectedIcon = isSelected ? (
        <SVG src={iconReviewsYes} />
    ) : (
        <SVG src={iconAddIcon} />
    );

    const buttonClasses = classNames({
        "button": true,
        "button--selected": isSelected,
    });
    return (
        <button
            className={buttonClasses}
            onClick={() => {
                onAttributeSelect(attribute, cardValue);
            }}
        >
            {isBooleanType && selectedIcon}
            {isSelected ? t`${buttonText}ed` : buttonText}
        </button>
    );
}

function renderTabCardButton(
    tabType: string,
    tabCard:
        | IComparisonCardProduct
        | IComparisonCardCategory
        | IComparisonCardAttribute,
    pageRootProducts: readonly IProduct[] | null,
    pageRootProductID: IProductID | null,
    pageCategorySelectors: readonly IPLCProductCategorySelector[],
    pageSelectedCategories: ISelectedCategories,
    pageSelectedOptionValues: IOptionValues,
    onProductSelect: (productNumber: IProductID) => void,
    onCategorySelect: (category: IPLCProductCategory) => void,
    onAttributeSelect: (
        attributeCode: IOptionCode,
        attributeValue: string,
    ) => void,
): React.JSX.Element | null {
    switch (tabType) {
        case "tab_product":
            return renderTabCardButtonProduct(
                tabCard as IComparisonCardProduct,
                pageRootProducts,
                pageRootProductID,
                onProductSelect,
            );
        case "tab_category":
            return renderTabCardButtonCategory(
                tabCard as IComparisonCardCategory,
                pageCategorySelectors,
                pageSelectedCategories,
                onCategorySelect,
            );
        case "tab_attribute":
            return renderTabCardButtonAttribute(
                tabCard as IComparisonCardAttribute,
                pageRootProducts,
                pageSelectedOptionValues,
                onAttributeSelect,
            );
        default:
            throw new Error(`Unexpected tab card type: ${tabType}`);
    }
}

function renderTabCards(
    tabType: string,
    tab:
        | IComparisonTabProduct
        | IComparisonTabCategory
        | IComparisonTabAttribute,
    viewport: IViewport,
    pageRootProducts: readonly IProduct[] | null,
    pageRootProductID: IProductID | null,
    pageCategorySelectors: readonly IPLCProductCategorySelector[],
    pageSelectedCategories: ISelectedCategories,
    pageSelectedOptionValues: IOptionValues,
    onProductSelect: (productNumber: IProductID) => void,
    onCategorySelect: (category: IPLCProductCategory) => void,
    onAttributeSelect: (
        attributeCode: IOptionCode,
        attributeValue: string,
    ) => void,
): React.JSX.Element[] {
    const tabCards = tab.tab_cards.map((tabCard, i: number) => {
        return (
            <div className={styles.tabCard} key={i} role="listitem">
                {tabCard.show_flair && (
                    <div className={styles.flair}>
                        <Flair flair={tabCard.card_flair} />
                    </div>
                )}
                <div className={styles.tabCardContent}>
                    {isMobileOrTabletWidth(viewport) ? (
                        <>
                            <div>
                                <Image
                                    className="responsive-img"
                                    src={
                                        tabCard.card_image_mobile
                                            ? tabCard.card_image_mobile
                                                  .renditions?.default?.url
                                            : tabCard.card_image.renditions
                                                  ?.default?.url
                                    }
                                    alt={tabCard.card_image.title}
                                    lazy={false}
                                />
                            </div>
                            <div>
                                <h4>
                                    <RichText html={tabCard.card_title || ""} />
                                </h4>
                                <RichText
                                    className={styles.tabCardCopy}
                                    html={tabCard.card_copy}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <h4>
                                <RichText html={tabCard.card_title || ""} />
                            </h4>
                            <div>
                                <Image
                                    className="responsive-img"
                                    src={
                                        tabCard.card_image.renditions?.default
                                            ?.url
                                    }
                                    alt={tabCard.card_image.title}
                                    lazy={false}
                                />
                            </div>
                            <RichText
                                className={styles.tabCardCopy}
                                html={tabCard.card_copy}
                            />
                        </>
                    )}
                    {renderTabCardButton(
                        tabType,
                        tabCard,
                        pageRootProducts,
                        pageRootProductID,
                        pageCategorySelectors,
                        pageSelectedCategories,
                        pageSelectedOptionValues,
                        onProductSelect,
                        onCategorySelect,
                        onAttributeSelect,
                    )}
                </div>
            </div>
        );
    });
    return tabCards;
}

function renderTabContent(
    tabCards: ITabCardsBlock,
    currentTab: number,
    viewport: IViewport,
    pageRootProducts: readonly IProduct[] | null,
    pageRootProductID: IProductID | null,
    pageCategorySelectors: readonly IPLCProductCategorySelector[],
    pageSelectedCategories: ISelectedCategories,
    pageSelectedOptionValues: IOptionValues,
    onProductSelect: (productNumber: IProductID) => void,
    onCategorySelect: (category: IPLCProductCategory) => void,
    onAttributeSelect: (
        attributeCode: IOptionCode,
        attributeValue: string,
    ) => void,
): React.JSX.Element | null {
    const tab = tabCards.tabs[currentTab];

    if (!tab) {
        return null;
    }
    const tabCopy = tab.value.tab_support_copy || "";
    return (
        <div className={styles.contentContainer}>
            {tab.value.tab_support_copy && (
                <div
                    className={styles.contentCopy}
                    dangerouslySetInnerHTML={{ __html: tabCopy }}
                ></div>
            )}
            <section className={styles.cards} role="list">
                {renderTabCards(
                    tab.type,
                    tab.value,
                    viewport,
                    pageRootProducts,
                    pageRootProductID,
                    pageCategorySelectors,
                    pageSelectedCategories,
                    pageSelectedOptionValues,
                    onProductSelect,
                    onCategorySelect,
                    onAttributeSelect,
                )}
            </section>
        </div>
    );
}

export const TabCardsBlock = (props: {
    theme?: string;
    tabCards: ITabCardsBlock;
    pageRootProducts: readonly IProduct[] | null;
    pageRootProductID: IProductID | null;
    pageProductNamespace: string | null;
    pageCategorySelectors: readonly IPLCProductCategorySelector[];
    pageOptionPanels: readonly IPLCOptionPanel[];
    pageSelectedCategories: ISelectedCategories;
    pageSelectedOptionValues: IOptionValues;
    viewport: IViewport;
    onCardProductSelect: (product: IProduct) => void;
    onCardCategorySelect: (payload: {
        selectorID: string;
        categoryID: IProductCategoryID;
        categorySelectors: readonly IPLCProductCategorySelector[];
        optionPanels: readonly IPLCOptionPanel[];
    }) => void;
    onCardAttributeSelect: (
        namespace: string,
        code: IOptionCode,
        index: number,
        totalNumValues: number,
        value: IOptionSingleValue,
    ) => void;
}): React.ReactNode => {
    const [currentTab, setCurrentTab] = React.useState(0);

    const onTabPress = (
        event: React.MouseEvent<HTMLElement>,
        tabNum: number,
    ): void => {
        event.preventDefault();
        setCurrentTab(tabNum);
    };

    const onTabSelect = (event: React.FormEvent<HTMLSelectElement>): void => {
        setCurrentTab(parseInt(event.currentTarget.value, 10));
    };

    const onCategorySelect = (category: IPLCProductCategory): void => {
        const selector = getCategorySelector(
            props.pageCategorySelectors,
            category,
        );

        if (selector) {
            props.onCardCategorySelect({
                selectorID: selector.id,
                categoryID: category.id,
                categorySelectors: props.pageCategorySelectors,
                optionPanels: props.pageOptionPanels,
            });
        }
    };

    const onAttributeSelect = (
        attributeCode: IOptionCode,
        attributeValue: string,
    ): void => {
        // First, check that the attributeCode exists in the configurator
        if (!hasOptionPanel(props.pageRootProducts, attributeCode)) {
            return;
        }

        if (!props.pageProductNamespace) {
            return;
        }

        props.onCardAttributeSelect(
            props.pageProductNamespace,
            attributeCode,
            0,
            1,
            attributeValue,
        );
    };

    const onProductSelect = async (
        productNumber: IProductID,
    ): Promise<void> => {
        const productURL = getProductURL(productNumber);
        const product = await getProduct(productURL);
        if (productNumber !== props.pageRootProductID) {
            props.onCardProductSelect(product);
        }
    };

    const themeName =
        props.theme && THEME_MAP[props.theme]
            ? THEME_MAP[props.theme]
            : props.theme; // Fallback to original if not in map

    const themeClass = themeName
        ? styles[themeName as keyof typeof styles]
        : "";
    const blockClasses = classNames(styles.root, themeClass);

    return (
        <section className={blockClasses}>
            <section>
                {renderHeader(props.tabCards, props.viewport)}
                {renderNav(
                    props.tabCards,
                    props.viewport,
                    currentTab,
                    onTabPress,
                    onTabSelect,
                )}
            </section>
            {renderTabContent(
                props.tabCards,
                currentTab,
                props.viewport,
                props.pageRootProducts,
                props.pageRootProductID,
                props.pageCategorySelectors,
                props.pageSelectedCategories,
                props.pageSelectedOptionValues,
                onProductSelect,
                onCategorySelect,
                onAttributeSelect,
            )}
        </section>
    );
};
